import { actions } from "@mixitone/actions";
import { Button, LoadingModal, ModalFooter, Spinner, Tooltip } from "@mixitone/components";
import { Club, ClubDocument, ClubPlayer } from "@mixitone/models";
import ApplicationController, { ApplicationView, batch } from "@mixitone/mvc";
import { List, subscribeList } from "@mixitone/oom";
import { formatDate, isPresent, withStateToggle } from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import AppModal from "components/common/AppModal";
import { AppController } from "controllers/AppController";
import TauriController from "controllers/TauriController";
import { injectModal } from "lib/injectParams";
import tauri from "lib/tauri";
import { messagesService } from "services";
import routeService from "services/RouteService";

interface ClubPlayerRequires {
  clubPlayer: ClubPlayer;
  field: string;
  reason: string;
}

interface State {
  loading: boolean;
  sending: boolean;
  club: Club;
  players: ClubPlayer[];
  documents: ClubDocument[];
  ids: string[];

  canPromote: ClubPlayer[];
  requires: Record<string, ClubPlayerRequires[]>;
  recentlyInvited: ClubPlayer[];
}

interface Props {
  clubId: string;
  ids: string;
}

class PromoteController extends ApplicationController<State, Props> {
  get initialState(): State {
    return {
      loading: true,
      sending: false,
      club: new Club({}),
      players: [],
      documents: [],
      ids: [],

      canPromote: [],
      requires: {},
      recentlyInvited: [],
    };
  }

  async initialize(props: Props) {
    this.state.ids = props.ids.split(",");
    await this.loadClub(props.clubId);
    await this.loadPlayers();
    this.state.loading = false;
  }

  get syncState() {
    if (tauri.enabled) {
      return this.get(TauriController).state.syncState;
    } else {
      return "idle";
    }
  }

  async loadClub(clubId: string) {
    this.state.club = await Club.query().find(clubId, { noCache: true });
    this.state.documents = ClubDocument.onlyLastVersion(
      await ClubDocument.query()
        .eq("club_id", clubId)
        .in("acceptance", ["new_members", "all_current_members"])
        .all(),
    );
    // Set the association as we won't use the other docs
    this.state.club.club_documents.set(this.state.documents);
  }

  async loadPlayers() {
    const query = ClubPlayer.query()
      .eq("club_id", this.state.club.id!)
      .in("id", this.state.ids)
      .preload("accepted_documents", "memberships");
    this.state.players = (await query.all()).filter((player) => !player.isCurrentMember);
    await this.loadPlayersReasons();

    const list = new List(...this.state.players);
    this.addDependency(
      await subscribeList(list, query, {
        callback: () => {
          this.loadPlayersReasons();
        },
      }),
    );
  }

  async loadPlayersReasons() {
    this.state.canPromote = [];
    this.state.requires = {};
    this.state.recentlyInvited = [];

    // Calculate date 7 days ago
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    batch(async () => {
      for (const player of this.state.players) {
        // Check if player was invited within the last 7 days
        if (player.invited_at && new Date(player.invited_at) > sevenDaysAgo) {
          this.state.recentlyInvited.push(player);
        } else {
          await this.loadPlayerReasons(player);
        }
      }
    });
  }

  async loadPlayerReasons(player: ClubPlayer) {
    player.club.set(this.state.club);

    const [canPromote, reasons] = await player.canBeMember();

    if (canPromote) {
      this.state.canPromote.push(player);
    } else {
      for (const reason of reasons) {
        if (!this.state.requires[reason.field]) this.state.requires[reason.field] = [];
        this.state.requires[reason.field].push({
          clubPlayer: player,
          field: reason.field,
          reason: reason.message,
        });
      }
    }
  }

  get invitablePlayers(): ClubPlayer[] {
    // Only include players who haven't been recently invited
    return [
      ...this.state.canPromote,
      ...(this.state.requires.documents?.map((req) => req.clubPlayer) || []),
    ].filter((p) => isPresent(p.email));
  }

  @withStateToggle("sending")
  async actionSendInvite() {
    try {
      await actions.notifications.createClubNotification({
        clubPlayerIds: this.invitablePlayers.map((p) => p.id!),
      });
      messagesService.addMessage("Invite sent successfully", "success");
      routeService.closeModal();
    } catch (error) {
      captureException(error);
      console.error("Failed to send invite:", error);
      messagesService.addMessage("Failed to send invite", "error");
    }
  }

  get canSendInvite() {
    if (this.state.sending) return false;
    if (this.invitablePlayers.length === 0) return false;
    if (this.syncState !== "idle") return false;

    const appController = this.get(AppController);
    const { online } = appController.state;
    return online;
  }
}

const PlayerButton = ApplicationView(({ player }: { player: ClubPlayer }) => {
  return (
    <Button
      kind="link"
      onClick={() =>
        routeService.openModal("/clubs/:clubId/players/:id/edit", {
          params: { clubId: player.club.id!, id: player.id! },
        })
      }
    >
      {player.name}
    </Button>
  );
});

const PromoteModal = () => {
  const controller = PromoteController.use();
  const { loading, sending, players, requires, canPromote, recentlyInvited } = controller.state;
  const { invitablePlayers, syncState: uploadState, canSendInvite } = controller;
  const appController = AppController.use();
  const { online } = appController.state;

  if (loading) return <LoadingModal title="Promote players" />;

  // Players that cannot be promoted due to missing data (not documents)
  const cannotPromote = Object.entries(requires).filter(([field]) => field !== "documents");

  // Players that need to accept documents
  const needDocuments = requires.documents || [];

  return (
    <AppModal title={`Promote ${players.length} players`}>
      <div className="space-y-6">
        {!online && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 text-yellow-800">
            You are currently offline. You can still review players, but you will need to be online to send
            invites.
          </div>
        )}

        {online && uploadState !== "idle" && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 text-yellow-800 flex items-center gap-2">
            <Spinner size={32} /> Updating player details
          </div>
        )}

        {/* Section 1: Players that can be promoted */}
        {canPromote.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Ready to promote ({canPromote.length})</h3>
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <ul className="space-y-2">
                {canPromote.map((player) => (
                  <li key={player.id} className="flex items-center text-green-800">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                    <PlayerButton player={player} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Section 2: Players that cannot be promoted due to missing data */}
        {cannotPromote.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Cannot promote - missing data</h3>
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              {cannotPromote.map(([field, playerRequires]) => (
                <div key={field} className="mb-4 last:mb-0">
                  <h4 className="font-medium text-red-800 mb-2 capitalize">Missing {field}:</h4>
                  <ul className="space-y-1 ml-4">
                    {playerRequires.map((req) => (
                      <li key={req.clubPlayer.id} className="flex items-center text-red-700">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>
                        <PlayerButton player={req.clubPlayer} />
                        &nbsp; - {req.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 3: Players that need to accept documents */}
        {needDocuments.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Need to accept documents ({needDocuments.length})
            </h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <ul className="space-y-2">
                {needDocuments.map((req) => (
                  <li key={req.clubPlayer.id} className="flex items-center justify-between text-yellow-800">
                    <div className="flex items-center">
                      <span className="w-2 h-2 bg-yellow-400 rounded-full mr-3"></span>
                      <PlayerButton player={req.clubPlayer} />
                    </div>
                    <span className="text-sm text-yellow-600">Must accept: {req.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Section 4: Players recently invited (within last 7 days) */}
        {recentlyInvited.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Recently invited ({recentlyInvited.length})
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-sm text-blue-600 mb-3">
                These players have been invited within the last 7 days
              </p>
              <ul className="space-y-2">
                {recentlyInvited.map((player) => (
                  <li key={player.id} className="flex items-center justify-between text-blue-800">
                    <div className="flex items-center">
                      <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                      <PlayerButton player={player} />
                    </div>
                    <span className="text-sm text-blue-600">Invited {formatDate(player.invited_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Show message if no players selected */}
        {players.length === 0 && (
          <div className="text-center py-8 text-gray-500">No players selected for promotion.</div>
        )}

        <ModalFooter>
          <div className="flex w-full justify-end items-center gap-2">
            <Button disabled={!canSendInvite} spinner={sending} onClick={() => controller.actionSendInvite()}>
              Send invite to {invitablePlayers.length} players
            </Button>
            <Tooltip icon>Email notifications can take up to 24 hours</Tooltip>
          </div>
        </ModalFooter>
      </div>
    </AppModal>
  );
};

export default injectModal(PromoteController.scope(ApplicationView(PromoteModal)));
